import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { usePermissionViewModel } from '../permissions/viewmodel/permission-viewmodel';

const BASEURL = process.env.REACT_APP_API_URL;

const RetourRecuPage = () => {
  const { hasPermission } = usePermissionViewModel('retour_recu');
  const canWrite = hasPermission('write');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]); // {code, status:'success'|'warning'|'error', message, at, etat}
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submitCode = async (rawCode) => {
    const codeLivraison = (rawCode ?? code).trim();
    if (!codeLivraison) return;
    if (!canWrite) { toast.error('Vous n\'avez pas la permission d\'enregistrer un retour reçu'); return; }
    setLoading(true);
    try {
      const res = await axios.patch(
        `${BASEURL}/BonCommande/MarkRetourRecu`,
        null,
        {
          params: { codeLivraison },
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        }
      );
      const data = res.data || {};
      const status = data.success ? 'success' : (data.alreadyReceived ? 'warning' : 'error');
      const message = data.message || (data.success ? 'Retour reçu enregistré' : 'Erreur');
      if (status === 'success') toast.success(message);
      else if (status === 'warning') toast.warning(message);
      else toast.error(message);
      setHistory((h) => [
        { code: codeLivraison, status, message, etat: data.etat, at: new Date() },
        ...h,
      ].slice(0, 50));
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.title || 'Commande introuvable';
      toast.error(msg);
      setHistory((h) => [
        { code: codeLivraison, status: 'error', message: msg, at: new Date() },
        ...h,
      ].slice(0, 50));
    } finally {
      setLoading(false);
      setCode('');
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitCode();
    }
  };

  const statusBadge = (s) => {
    if (s === 'success') return 'bg-green-100 text-green-700';
    if (s === 'warning') return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Retour Reçu</h1>
        <p className="text-sm text-gray-500 mt-1">
          Scannez le code de livraison d'une commande en statut <span className="font-semibold">Retour</span> pour la marquer comme <span className="font-semibold">Retour reçu</span>.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Code de livraison
        </label>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={canWrite ? "Scannez ou saisissez le code livraison..." : "Permission insuffisante"}
            autoFocus
            disabled={loading || !canWrite}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#174540] focus:border-transparent text-lg disabled:bg-gray-100"
          />
          <button
            type="button"
            onClick={() => submitCode()}
            disabled={loading || !code.trim() || !canWrite}
            className="px-6 py-3 bg-[#174540] text-white font-medium rounded-md hover:bg-[#0d2926] disabled:opacity-50"
          >
            {loading ? 'Traitement...' : 'Valider'}
          </button>
        </div>
        {!canWrite && (
          <p className="text-xs text-red-600 mt-2">
            Vous n'avez pas la permission « retour reçu » (écriture).
          </p>
        )}
        <p className="text-xs text-gray-500 mt-2">
          Astuce : utilisez un lecteur code-barres, le champ se valide automatiquement à la touche Entrée.
        </p>
      </div>

      {history.length > 0 && (
        <div className="bg-white shadow rounded-lg mt-6 overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Historique de la session</h2>
            <button
              type="button"
              onClick={() => setHistory([])}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Effacer
            </button>
          </div>
          <ul className="divide-y divide-gray-100">
            {history.map((h, idx) => (
              <li key={idx} className="px-6 py-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge(h.status)}`}>
                    {h.status === 'success' ? 'OK' : h.status === 'warning' ? 'Déjà reçu' : 'Erreur'}
                  </span>
                  <span className="font-mono text-gray-900">{h.code}</span>
                  <span className="text-gray-500">{h.message}</span>
                </div>
                <span className="text-gray-400 text-xs">
                  {h.at.toLocaleTimeString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RetourRecuPage;
